import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useFlightLog } from '../contexts/FlightLogContext';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../config';
import { Plane, ArrowRight, Sparkles } from 'lucide-react';
import AuthModal from './AuthModal';

const QUICK_FLIGHTS = ['EK101', 'SV001', 'QR567', 'TK1', 'BA003', 'EY101'];

/**
 * Global flight log modal — opened from the navbar quick-log button or mobile FAB.
 * Sits at the App root level so it's available on every page.
 */
export default function FlightLogModal() {
  const { isOpen, close } = useFlightLog();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [flightNum, setFlightNum] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  const handleClose = () => { close(); setFlightNum(''); };

  const handleCheckin = async () => {
    if (!flightNum.trim()) { toast.error('Enter a flight number'); return; }
    if (!user) { close(); setShowAuth(true); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/flights/checkin`, { flight_number: flightNum }, { withCredentials: true });
      toast.success(`✈ ${flightNum.toUpperCase()} logged!`);
      handleClose();
      navigate(`/route/${res.data.route_id}`);
    } catch {
      toast.error('Check-in failed — try again');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <div className="w-7 h-7 rounded-lg bg-[#FF4500]/15 flex items-center justify-center">
                <Plane className="w-4 h-4 text-[#FF4500]" />
              </div>
              Log Your Flight
            </DialogTitle>
          </DialogHeader>

          <div className="py-1">
            <p className="text-sm text-muted-foreground mb-4">
              Enter your flight number to join the route community and share your experience.
            </p>

            <div className="flex gap-2 mb-4">
              <Input
                placeholder="e.g. EK101, SV001, QR567"
                value={flightNum}
                onChange={e => setFlightNum(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                onKeyDown={e => e.key === 'Enter' && handleCheckin()}
                className="flex-1 text-base font-mono uppercase tracking-wide"
                data-testid="global-flight-number-input"
                autoFocus
              />
              <Button
                onClick={handleCheckin}
                disabled={loading || !flightNum.trim()}
                className="fly-btn-glossy px-4"
                data-testid="global-flight-checkin-btn"
              >
                {loading ? (
                  <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4" />
                )}
              </Button>
            </div>

            {/* Quick-pick popular flights */}
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-2 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Popular flights
              </p>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_FLIGHTS.map(fn => (
                  <button
                    key={fn}
                    onClick={() => setFlightNum(fn)}
                    className={`text-xs px-2.5 py-1 rounded-full font-mono transition-all border ${
                      flightNum === fn
                        ? 'bg-[#FF4500] text-white border-[#FF4500]'
                        : 'bg-muted hover:bg-[#FF4500]/10 hover:text-[#FF4500] hover:border-[#FF4500]/30 border-border'
                    }`}
                  >
                    {fn}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Auth redirect for guests */}
      <AuthModal open={showAuth} onOpenChange={setShowAuth} defaultTab="register" />
    </>
  );
}
